import React, { useEffect, useState } from "react";
import { Download, Trash2, FileDown } from "lucide-react";
import { Tooltip, Popconfirm, Input } from "antd";

import {
  useAllOrders,
  useCancelOrder,
  useDownloadLabel,
  useDownloadManifest,
  useSearchOrders,
} from "../../../../../api/orders/orders";
import { useLoader } from "../../../../../utils/LoaderContext";

const ORDERS_PER_PAGE = 20;

const Manifest = ({ status = "manifested" }) => {
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrders, setSelectedOrders] = useState([]); // ✅ Store shipcluesId instead of awbNumber

  const { setLoading } = useLoader();

  const {
    data,
    isLoading,
    refetch: refetchAll,
  } = useAllOrders(
    { currentPage, ORDERS_PER_PAGE, status },
    {
      enabled: !searchQuery,
    }
  );

  const { data: searchData, isLoading: isSearchLoading } = useSearchOrders(
    searchQuery,
    status,
    { enabled: !!searchQuery }
  );

  const { mutate: cancelOrder } = useCancelOrder();
  const { mutate: downloadLabel, isLoading: downloading } = useDownloadLabel();
  const { mutate: downloadManifest, isLoading: downloadingManifest } =
    useDownloadManifest();

  // Debounce search
  useEffect(() => {
    const delay = setTimeout(() => {
      setSearchQuery(searchText.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(delay);
  }, [searchText]);

  // Refetch when search is cleared
  useEffect(() => {
    if (!searchQuery) {
      setLoading(true);
      refetchAll().finally(() => setLoading(false));
    }
  }, [searchQuery, refetchAll, setLoading]);

  const orders = searchQuery ? searchData || [] : data?.orders || [];
  const filteredOrders = orders || [];

  const totalPages = searchQuery
    ? 1
    : Math.ceil(filteredOrders.length / ORDERS_PER_PAGE);

  const paginatedOrders = searchQuery
    ? filteredOrders
    : filteredOrders.slice(
        (currentPage - 1) * ORDERS_PER_PAGE,
        currentPage * ORDERS_PER_PAGE
      );

  const isLoadingCombined = isLoading || (searchQuery && isSearchLoading);

  // Handle Select All - ✅ Store shipcluesId
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOrders(
        paginatedOrders
          .filter((order) => order.shipcluesId) // ✅ Only select orders with shipcluesId
          .map((order) => order.shipcluesId)
      );
    } else {
      setSelectedOrders([]);
    }
  };

  // Handle Single Select - ✅ Use shipcluesId
  const handleSelectOne = (shipcluesId) => {
    console.log("7t67t",shipcluesId)
    setSelectedOrders((prev) =>
      prev.includes(shipcluesId)
        ? prev.filter((id) => id !== shipcluesId)
        : [...prev, shipcluesId]
    );
  };

  // Download all selected labels
  const handleDownloadAll = () => {
    downloadLabel(selectedOrders); // ✅ Pass array of shipcluesIds
  };

  return (
    <div className="w-full mt-6">
      {/* Search + Download All */}
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by AWB, Order Number or Name"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1);
          }}
        />
        {selectedOrders.length > 0 && (
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded disabled:opacity-50"
            onClick={handleDownloadAll}
            disabled={downloading}
          >
            {downloading
              ? "Downloading..."
              : `Download Selected (${selectedOrders.length})`}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="w-full overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
        <table className="min-w-[1500px] table-auto">
          <thead className="bg-gray-50 text-xs font-semibold text-gray-700 uppercase">
            <tr>
              <th className="p-3">
                <input
                  type="checkbox"
                  checked={
                    selectedOrders.length ===
                      paginatedOrders.filter((o) => o.shipcluesId).length &&
                    paginatedOrders.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </th>
              <th className="p-3">Order Date</th>
              <th className="p-3">Manifest ID</th>
              <th className="p-3">Order ID / Status</th>
              <th className="p-3">Payment</th>
              <th className="p-3">Products</th>
              <th className="p-3">Customer</th>
              <th className="p-3">Address</th>
              <th className="p-3">Courier / Tracking</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white text-sm">
            {isLoadingCombined ? (
              <tr>
                <td colSpan="10" className="text-center py-6 text-gray-500">
                  Loading orders...
                </td>
              </tr>
            ) : paginatedOrders.length === 0 ? (
              <tr>
                <td colSpan="10" className="text-center py-6 text-gray-400">
                  No orders found.
                </td>
              </tr>
            ) : (
              paginatedOrders.map((order, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="p-4">
                    {order.shipcluesId ? (
                      <input
                        type="checkbox"
                        checked={selectedOrders.includes(order.shipcluesId)}
                        onChange={() => handleSelectOne(order.shipcluesId)}
                      />
                    ) : (
                      <span className="text-gray-400 text-xs">N/A</span>
                    )}
                  </td>
                  <td className="p-4">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>
                  <td className="p-4 text-blue-600 font-medium">
                    {order.manifestId || "-"}
                  </td>
                  <td className="p-4">
                    <div className="font-medium text-blue-600">
                      {order.order_number}
                    </div>
                    <div
                      className={`text-xs font-semibold px-2 py-1 rounded inline-block mt-1 ${
                        order.shippingStatus === "cancelled"
                          ? "bg-red-100 text-red-600"
                          : order.shippingStatus === "in_transit"
                          ? "bg-blue-100 text-blue-600"
                          : order.shippingStatus === "shipped"
                          ? "bg-green-100 text-green-600"
                          : "bg-yellow-100 text-yellow-600"
                      }`}
                    >
                      {order.shippingStatus}
                    </div>
                  </td>
                  <td className="p-4">
                    ₹{order.total_amount}
                    <div className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded mt-1 inline-block">
                      {order.PaymentType}
                    </div>
                  </td>
                  <td className="p-4">
                    {order.ProductDetails?.map((prod, i) => (
                      <p key={i} className="truncate">
                        {prod.name}
                      </p>
                    ))}
                  </td>
                  <td className="p-4">
                    <p>{order.CustomerName}</p>
                    <p>{order.BillingAddress?.phone}</p>
                  </td>
                  <td className="p-4 text-sm">
                    <p>{order.BillingAddress?.city}</p>
                    <p>{order.BillingAddress?.state}</p>
                    <p>{order.BillingAddress?.pincode}</p>
                  </td>
                  <td className="p-4">
                    <p className="font-medium">{order?.courier || "N/A"}</p>
                    {order.awbNumber ? (
                      <a
                        href={`https://shipclues.com/track-order/${order.awbNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors duration-200 mt-1"
                      >
                        Track Order
                      </a>
                    ) : (
                      <span className="inline-block px-3 py-1 bg-gray-100 text-gray-500 text-sm rounded mt-1">
                        AWB Not Available
                      </span>
                    )}
                  </td>
                  <td className="p-4 flex flex-wrap gap-2">
                    {order.shipcluesId && (
                      <Tooltip title="Download Label">
                        <button
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm disabled:opacity-50"
                          onClick={() => downloadLabel(order.shipcluesId)} // ✅ Pass shipcluesId
                          disabled={downloading}
                        >
                          <Download size={16} />
                        </button>
                      </Tooltip>
                    )}
                    {order.manifestId && (
                      <Tooltip title="Download Manifest">
                        <button
                          className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded text-sm disabled:opacity-50"
                          onClick={() => downloadManifest(order.manifestId)}
                          disabled={downloadingManifest}
                        >
                          <FileDown size={16} />
                        </button>
                      </Tooltip>
                    )}
                    {order.shipcluesId && (
                      <Tooltip title="Cancel Order">
                        <Popconfirm
                          title="Are you sure you want to cancel this order?"
                          onConfirm={() => cancelOrder(order.shipcluesId)}
                          okText="Yes"
                          cancelText="No"
                          placement="topRight"
                        >
                          <button className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded">
                            <Trash2 size={16} />
                          </button>
                        </Popconfirm>
                      </Tooltip>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!searchQuery && !isLoadingCombined && totalPages > 1 && (
        <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
          <p>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </p>
          <div className="space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded border ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded border ${
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Manifest;
